import { ECoinTypeAvailabilityMode } from './constants'
import {
    requestGetAllPairs,
    requestGetAvailableCurrencies,
    requestGetAvailablePairs,
    requestGetVolumes,
    TBulkPairApiDto,
    TCurrencyApiDto, TPairApiDto,
    TPairVolumeApiDto,
} from './api'
import { getIsPairCustomExcluded } from './checks'

import { ECoinInputType } from 'nexchange-service/module/exchangeWidget'
import { appConfigContext } from 'nexchange-service/appServiceConfig'


interface ICurrencyDto {
    code: string
    name: string
    isCrypto: boolean
    extraId: string | null
    minConfirmations: number
    hasEnabledPairs: boolean
    isEnabledAsBase: boolean
    isEnabledAsQuote: boolean
}

interface ICurrencyWithVolumeDto extends ICurrencyDto {
    fromVolumeUsd: number
    fromVolumeBtc: number
    toVolumeUsd: number
    toVolumeBtc: number
    volumeUsd: number
    volumeBtc: number
}

type TCurrencyDtoRecord = Record<string, ICurrencyDto>
type TCurrencyWithVolumeRecord = Record<string, ICurrencyWithVolumeDto>

interface IAvailablePairDto {
    name: string
    minAmountFrom: number
    maxAmountFrom: number
    minAmountTo: number
    maxAmountTo: number
    fromCurrencyDto: ICurrencyDto
    toCurrencyDto: ICurrencyDto

    fromVolumeUsd: number
    fromVolumeBtc: number
    toVolumeUsd: number
    toVolumeBtc: number
}

enum EPaymentMode {
    BUY = 'buy',
    SELL = 'sell'
}

interface IAvailablePairDtoWithModeType extends IAvailablePairDto {
    paymentMode?: EPaymentMode
}

interface IValidatedPairsDto {
    fromPairs: Record<string, string[]>
    toPairs: Record<string, string[]>
    allAvailablePairs: IAvailablePairDto[]
}

interface IValidatedPairsDtoWithoutAmount {
    fromPairs: Record<string, string[]>
    toPairs: Record<string, string[]>
    allAvailablePairs: Omit<IAvailablePairDto, "minAmountFrom" | "maxAmountFrom" | "minAmountTo" | "maxAmountTo">[]
}

const mapCurrencyModelToDto = (currencyApiDto: TCurrencyApiDto): ICurrencyDto => {
    const {
        code,
        name,
        min_confirmations,
        is_crypto,
        extra_id,
        is_base_of_enabled_pair,
        is_quote_of_enabled_pair,
        has_enabled_pairs,
    } = currencyApiDto

    return <ICurrencyDto>{
        code,
        name,
        isCrypto: is_crypto,
        extraId: extra_id,
        minConfirmations: min_confirmations,
        hasEnabledPairs: has_enabled_pairs,
        isEnabledAsBase: is_base_of_enabled_pair,
        isEnabledAsQuote: is_quote_of_enabled_pair,
    }
}

const mapAvailableCurrencyApiDtoToFrontendDto = (
    availableCurrencies: TCurrencyApiDto[],
): ICurrencyDto[] => availableCurrencies.map(mapCurrencyModelToDto) as ICurrencyDto[]

const getPairCurrencyDto = ({
    availableCurrencyDtoList,
    fromCode,
    toCode,
}: {
    availableCurrencyDtoList: ICurrencyDto[]
    fromCode: string
    toCode: string
}): { fromCurrencyDto; toCurrencyDto } => {
    let fromCurrencyDto = null
    let toCurrencyDto = null

    for (const currencyDto of availableCurrencyDtoList) {
        const { code } = currencyDto

        if (code === fromCode) {
            fromCurrencyDto = currencyDto
        } else if (code === toCode) {
            toCurrencyDto = currencyDto
        }

        if (fromCurrencyDto && toCurrencyDto) break
    }

    return { fromCurrencyDto, toCurrencyDto }
}

const isCoinCompatibleWithTypeMode = (
    currencyCompatabilityTypeMode = ECoinTypeAvailabilityMode.BOTH,
    isCryptoCurrency,
) => {
    switch (currencyCompatabilityTypeMode) {
        case ECoinTypeAvailabilityMode.CRYPTO:
            return isCryptoCurrency

        case ECoinTypeAvailabilityMode.FIAT:
            return !isCryptoCurrency

        case ECoinTypeAvailabilityMode.BOTH:
        default:
            return true
    }
}

const updateCurrencyMapWithVolumes = ({
    currencyWithVolumeRecord,
    currencyDto,
    pairInputType,
    volumeUsd = 0,
    volumeBtc = 0,
}: {
    currencyWithVolumeRecord: TCurrencyWithVolumeRecord
    currencyDto: ICurrencyDto
    pairInputType: ECoinInputType
    volumeUsd: number
    volumeBtc: number
}) => {
    const currencyCode = currencyDto.code
    const fromCurrencyEntry = currencyWithVolumeRecord[currencyCode]

    const currencyVolumes = {
        fromVolumeUsd: pairInputType === ECoinInputType.FROM ? volumeUsd : 0,
        fromVolumeBtc: pairInputType === ECoinInputType.FROM ? volumeBtc : 0,
        toVolumeUsd: pairInputType === ECoinInputType.TO ? volumeUsd : 0,
        toVolumeBtc: pairInputType === ECoinInputType.TO ? volumeBtc : 0,
        volumeUsd: volumeUsd,
        volumeBtc: volumeBtc,
    }

    if (fromCurrencyEntry) {
        currencyVolumes.fromVolumeUsd += fromCurrencyEntry.fromVolumeUsd
        currencyVolumes.fromVolumeBtc += fromCurrencyEntry.fromVolumeBtc
        currencyVolumes.toVolumeUsd += fromCurrencyEntry.toVolumeUsd
        currencyVolumes.toVolumeBtc += fromCurrencyEntry.toVolumeBtc
        currencyVolumes.volumeUsd += fromCurrencyEntry.volumeUsd
        currencyVolumes.volumeBtc += fromCurrencyEntry.volumeBtc
    }

    currencyWithVolumeRecord[currencyCode] = <ICurrencyWithVolumeDto>{
        ...currencyDto,
        ...currencyVolumes,
    }
}
const updateCurrencyMapWithVolumesByPairCoins = ({
    currencyWithVolumeRecord,
    fromCurrencyDto,
    toCurrencyDto,
    fromVolumeUsd,
    fromVolumeBtc,
    toVolumeUsd,
    toVolumeBtc,
}: {
    currencyWithVolumeRecord: TCurrencyWithVolumeRecord
    fromCurrencyDto: ICurrencyDto
    toCurrencyDto: ICurrencyDto
    fromVolumeUsd: number
    fromVolumeBtc: number
    toVolumeUsd: number
    toVolumeBtc: number
}) => {
    updateCurrencyMapWithVolumes({
        currencyWithVolumeRecord,
        currencyDto: fromCurrencyDto,
        volumeUsd: fromVolumeUsd,
        volumeBtc: fromVolumeBtc,
        pairInputType: ECoinInputType.FROM,
    })

    updateCurrencyMapWithVolumes({
        currencyWithVolumeRecord,
        currencyDto: toCurrencyDto,
        volumeUsd: toVolumeUsd,
        volumeBtc: toVolumeBtc,
        pairInputType: ECoinInputType.TO,
    })
}

const sortCurrencyByVolume = (a: ICurrencyWithVolumeDto, b: ICurrencyWithVolumeDto) => {
    const { volumeBtc: VolumeBtcA } = a
    const { volumeBtc: VolumeBtcB } = b

    if (VolumeBtcA > VolumeBtcB) return -1
    if (VolumeBtcA < VolumeBtcB) return 1
    return 0
}

const sortCurrencyByCustomCodes = (
    a: ICurrencyWithVolumeDto, b: ICurrencyWithVolumeDto, customCurrencyCodes: string[] = []
) => {
    const {
        CUSTOM_POPULAR_COINS
    } = appConfigContext.getAppConfig()
    if (!CUSTOM_POPULAR_COINS || CUSTOM_POPULAR_COINS.length == 0) return 0
    const { code: currencyCodeA } = a
    const { code: currencyCodeB } = b

    if (currencyCodeA && currencyCodeB) {
        if (CUSTOM_POPULAR_COINS.includes(currencyCodeA) && !CUSTOM_POPULAR_COINS.includes(currencyCodeB)) {
            return -1
        } else if (!CUSTOM_POPULAR_COINS.includes(currencyCodeA) && CUSTOM_POPULAR_COINS.includes(currencyCodeB)) {
            return 1
        }
    }
    return 0
}

const mapValidPairsAndCurrencyVolumes = ({
    coinTypeAvailabilityModeFrom,
    coinTypeAvailabilityModeTo,
    availablePairs,
    availableCurrencyDtoList,
    pairVolumes,
}: {
    coinTypeAvailabilityModeFrom: ECoinTypeAvailabilityMode
    coinTypeAvailabilityModeTo: ECoinTypeAvailabilityMode
    availablePairs:  TPairApiDto[]
    availableCurrencyDtoList: ICurrencyDto[]
    pairVolumes: TPairVolumeApiDto[]
}): {
    validatedPairsDto: IValidatedPairsDtoWithoutAmount
    currencyWithVolumeRecord: TCurrencyWithVolumeRecord
} => {
    const validatedPairsDto = <IValidatedPairsDtoWithoutAmount>{
        fromPairs: {} as Record<string, string[]>,
        toPairs: {} as Record<string, string[]>,
        allAvailablePairs: [] as Omit<IAvailablePairDto, "minAmountFrom" | "maxAmountFrom" | "minAmountTo" | "maxAmountTo">[],
    }
    const currencyWithVolumeRecord = {} as TCurrencyWithVolumeRecord

    if (!availablePairs) return { validatedPairsDto, currencyWithVolumeRecord }

    // forEach used for better performance since this function is used in initial rendering
    availablePairs.forEach(
        ({
            name: pairName,
            base: to,
            quote: from,
        }) => {
            if (getIsPairCustomExcluded(to, from)) return

            const { fromCurrencyDto, toCurrencyDto } = getPairCurrencyDto({
                availableCurrencyDtoList,
                fromCode: from,
                toCode: to,
            })

            if (!fromCurrencyDto || !toCurrencyDto) return

            // Parse pair / coin volume data:
            const pairVolumeDto = pairVolumes.find(
                pairVolumeApiDto => pairVolumeApiDto.pair.name === pairName,
            )

            const {
                base_volume: toVolumeUsd,
                quote_volume: fromVolumeUsd,
                base_volume_btc: toVolumeBtc,
                quote_volume_btc: fromVolumeBtc,
            } = pairVolumeDto ?? {
                base_volume: 0,
                quote_volume: 0,
                base_volume_btc: 0,
                quote_volume_btc: 0,
            }

            // =========== VALIDATE AVAILABLE PAIRS ============
            // Make coin available pair coins map:
            const isFromCompatibleWithTypeMode = isCoinCompatibleWithTypeMode(
                coinTypeAvailabilityModeFrom,
                fromCurrencyDto.isCrypto,
            )
            const isToCompatibleWithTypeMode = isCoinCompatibleWithTypeMode(
                coinTypeAvailabilityModeTo,
                toCurrencyDto.isCrypto,
            )

            if (!isFromCompatibleWithTypeMode || !isToCompatibleWithTypeMode) {
                return
            }

            // =========== ADD DATA ============
            // sum-up from/to volumes for all pairs
            updateCurrencyMapWithVolumesByPairCoins({
                currencyWithVolumeRecord,
                fromCurrencyDto,
                toCurrencyDto,
                fromVolumeUsd,
                fromVolumeBtc,
                toVolumeUsd,
                toVolumeBtc,
            })

            // Make available pairs list:
            validatedPairsDto.allAvailablePairs.push({
                name: pairName,
                fromCurrencyDto,
                toCurrencyDto,
                fromVolumeUsd,
                fromVolumeBtc,
                toVolumeUsd,
                toVolumeBtc,
            })

            if (isFromCompatibleWithTypeMode) {
                if (Array.isArray(validatedPairsDto.fromPairs[from])) {
                    validatedPairsDto.fromPairs[from].push(to)
                } else {
                    validatedPairsDto.fromPairs[from] = [to]
                }
            }

            if (isToCompatibleWithTypeMode) {
                if (Array.isArray(validatedPairsDto.toPairs[to])) {
                    validatedPairsDto.toPairs[to].push(from)
                } else {
                    validatedPairsDto.toPairs[to] = [from]
                }
            }
        },
    )

    return {
        validatedPairsDto,
        currencyWithVolumeRecord,
    }
}

const getValidPairsAndCurrencies = async ({
    coinTypeAvailabilityModeFrom,
    coinTypeAvailabilityModeTo,
}: {
    coinTypeAvailabilityModeFrom: ECoinTypeAvailabilityMode
    coinTypeAvailabilityModeTo: ECoinTypeAvailabilityMode
}): Promise<{
    availableCurrencyDtoList: ICurrencyDto[]
    validatedPairsDto: IValidatedPairsDtoWithoutAmount
    currencyWithVolumeRecord: TCurrencyWithVolumeRecord
}> => {
    const fiatOnly = coinTypeAvailabilityModeFrom == ECoinTypeAvailabilityMode.FIAT ||
        coinTypeAvailabilityModeTo == ECoinTypeAvailabilityMode.FIAT
    const [
        { data: availablePairs, error: availablePairsError },
        { data: allCurrencies, error: availableCurrenciesError },
        { data: volumesDto, error: volumesError },
    ] = await Promise.all([
        requestGetAllPairs(),
        requestGetAvailableCurrencies(),
        requestGetVolumes(),
    ])

    const availableCurrencyDtoList = mapAvailableCurrencyApiDtoToFrontendDto(allCurrencies)
    const pairVolumes: TPairVolumeApiDto[] = volumesDto.tradable_pairs

    const { validatedPairsDto, currencyWithVolumeRecord } = mapValidPairsAndCurrencyVolumes({
        coinTypeAvailabilityModeFrom,
        coinTypeAvailabilityModeTo,
        availablePairs,
        availableCurrencyDtoList,
        pairVolumes,
    })

    // TODO: check why availableCurrencyDtoList and currencyWithVolumeRecord is not same length
    // const finalCurrencyList = availableCurrencyDtoList.filter(currency =>
    //     Boolean(currencyWithVolumeRecord[currency.code]),
    // )

    return {
        availableCurrencyDtoList: availableCurrencyDtoList,
        validatedPairsDto,
        currencyWithVolumeRecord,
    }
}

export type {
    ICurrencyDto,
    IAvailablePairDto,
    IAvailablePairDtoWithModeType,
    IValidatedPairsDto,
    TCurrencyDtoRecord,
    TCurrencyWithVolumeRecord,
    IValidatedPairsDtoWithoutAmount
}
export { getValidPairsAndCurrencies, sortCurrencyByVolume, sortCurrencyByCustomCodes, EPaymentMode }
